import {
  BANNER_BG, BANNER_H, COL_LETTER_FS, LETTER_STRIP_H,
  chainKey, isLightColor, seatColor, sebShort,
} from "../lib/geometry";
import type { FleetGeometry } from "../lib/geometry";
import { adbParentFs, isHighlighted } from "../lib/selection";
import type { Selection } from "../lib/selection";
import { availColor } from "../lib/types/demo";
import type { TripSeat } from "../lib/types/demo";
import type { TopologyPayload } from "../lib/types/topology";
import type { ViewMode } from "../lib/viewMode";


interface SeatGridProps {
  geometry: FleetGeometry;
  topology: TopologyPayload;
  selection?: Selection;
  onSelect?: (s: Selection | undefined) => void;
  /** Double-click = select AND jump the LOPA to the highlighted seats. */
  onActivate?: (s: Selection) => void;
  viewMode: ViewMode;
  /** Availability view: per-seat scores for the selected demo trip. */
  avail?: Record<string, TripSeat>;
  /** Incident selection: explicit bright set overrides chain highlighting. */
  brightSeats?: Set<string> | null;
}


// Corner labels for the seat tile: which parent feeds it (top-left) and on
// which port (top-right), matching the active view's semantics.
function cornerLabels(
  info: TopologyPayload["seats"][string],
  isEx3: boolean,
  viewMode: ViewMode,
): { parent: string; port: string } | null {
  if (isEx3) {
    if (info.nc === undefined || info.nc === null) return null;
    return { parent: `NC${info.nc}`, port: `P${info.port}` };
  }
  if (viewMode === "mcu") {
    if (info.mcu === undefined || info.mcu === null) return null;
    // Seat data is 0-based; hardware designations ("MCU 001") are 1-based.
    return { parent: `MCU${info.mcu + 1}`, port: `P${info.port}` };
  }
  if (info.chain_kind && info.chain_num !== undefined) {
    return {
      parent: `${info.chain_kind}${info.chain_num}`,
      port: info.chain_port !== undefined ? `P${info.chain_port}` : "",
    };
  }
  return null;
}


// "Seat 39G — FS 1 → ADB 2 → Port 4 → SEB 5 · MCU 2 / P3"
function seatTooltip(
  seatId: string,
  info: TopologyPayload["seats"][string],
  isEx3: boolean,
  topology: TopologyPayload,
): string {
  if (isEx3) {
    return `Seat ${seatId} — NC ${info.nc} → Port ${info.port} → HPS ${info.hps ?? "?"}`;
  }
  const parts: string[] = [];
  if (info.chain_kind && info.chain_num !== undefined) {
    if (info.chain_kind === "ADB") {
      const fs = adbParentFs(topology, info.chain_num);
      if (fs !== null) parts.push(`FS ${fs}`);
    }
    parts.push(`${info.chain_kind} ${info.chain_num}`);
    if (info.chain_port !== undefined) parts.push(`Port ${info.chain_port}`);
    if (info.chain_seb !== undefined) parts.push(`SEB ${info.chain_seb}`);
  }
  const data = parts.length ? parts.join(" → ") : "no chain data";
  const mcuDisp = info.mcu !== undefined && info.mcu !== null ? info.mcu + 1 : "?";
  return `Seat ${seatId} — ${data} · power MCU ${mcuDisp} / P${info.port}`;
}


export default function SeatGrid({
  geometry, topology, selection, onSelect, onActivate, viewMode,
  avail, brightSeats,
}: SeatGridProps) {
  const isAvail = viewMode === "avail";
  const { layout, seats } = topology;
  const {
    rows, sections, gridLeft, masterGridW, cabinGrids, rowYs,
  } = geometry;

  const cellW = layout.canvas.cell_w;
  const cellH = layout.canvas.cell_h;
  const rowLabelFs = layout.canvas.row_label_fs;
  const rowLabelY  = layout.canvas.row_label_y;
  const topo = layout.topology;

  const bannerLeft  = gridLeft;
  const bannerRight = gridLeft + masterGridW;
  const bannerW     = bannerRight - bannerLeft;
  const selectedSeatId = selection?.kind === "seat" ? selection.seatId : undefined;

  return (
    <g>
      {sections.map((s) => {
        const grid = cabinGrids.get(s.cabinName);
        const cabinLayout =
          layout.cabins[s.cabinName]?.layout ?? layout.columns.master;
        const letters = cabinLayout.filter((c) => c !== "|");
        const letterY = s.bannerY + BANNER_H + LETTER_STRIP_H / 2 + 4;

        return (
          <g key={`banner-${s.cabinName}-${s.bannerY}`}>
            <rect
              x={bannerLeft}
              y={s.bannerY + 4}
              width={bannerW}
              height={BANNER_H - 8}
              fill={BANNER_BG}
              stroke="rgba(15,23,42,0.06)"
              strokeWidth={0.5}
              rx={5}
            />
            <text
              x={bannerLeft + bannerW / 2}
              y={s.bannerY + BANNER_H / 2 + 4}
              textAnchor="middle"
              fontSize={12}
              fontWeight={700}
              letterSpacing={2.4}
              fill="#475569"
            >
              {s.cabinName.toUpperCase()}
            </text>
            <text
              x={bannerRight - 14}
              y={s.bannerY + BANNER_H / 2 + 4}
              textAnchor="end"
              fontSize={10.5}
              fontWeight={600}
              fill="#94A3B8"
            >
              {s.seatCount} seats
            </text>
            {grid &&
              letters.map((letter) => {
                const x = grid.colX[letter];
                if (x === undefined) return null;
                return (
                  <text
                    key={letter}
                    x={x + cellW / 2}
                    y={letterY}
                    textAnchor="middle"
                    fontSize={COL_LETTER_FS}
                    fontWeight={700}
                    fill="#5D6D7E"
                    letterSpacing="0.4"
                  >
                    {letter}
                  </text>
                );
              })}
          </g>
        );
      })}

      {rows.map(({ rn, cabinName, cabinLetters }) => {
        const yTop = rowYs.get(rn)!;
        const grid = cabinGrids.get(cabinName);
        if (!grid) return null;
        const anchors =
          grid.aisles.length > 0
            ? grid.aisles
            : [gridLeft + masterGridW / 2];

        return (
          <g key={rn}>
            {anchors.map((ax, i) => (
              <text
                key={i}
                x={ax}
                y={yTop + rowLabelY}
                textAnchor="middle"
                fontSize={rowLabelFs}
                fontWeight={600}
                fill="#5D6D7E"
              >
                {rn}
              </text>
            ))}

            {cabinLetters.map((letter) => {
              const xpos = grid.colX[letter];
              if (xpos === undefined) return null;

              const info = seats[`${rn}${letter}`];
              if (!info) {
                return (
                  <rect
                    key={letter}
                    x={xpos + 3}
                    y={yTop + 3}
                    width={cellW - 6}
                    height={cellH - 6}
                    fill="rgba(15,23,42,0.03)"
                    stroke="rgba(15,23,42,0.06)"
                    strokeWidth={0.5}
                    rx={4}
                  />
                );
              }

              const seatId = `${rn}${letter}`;
              const isEx3 = topo.type === "nc-port";
              const availPct = isAvail
                ? avail?.[seatId]?.availability_pct
                : undefined;
              // Data view colours by data chain when parsed; MCU view (and
              // fleets without chain data) keep the power-zone palette.
              // Availability view recolours by the trip score.
              const ck = viewMode === "data" ? chainKey(info) : null;
              const colour = isAvail
                ? availColor(availPct)
                : (ck && geometry.chainColorByKey[ck]) || seatColor(info, topo);
              const label = isEx3 ? `HPS ${info.hps ?? ""}` : sebShort(info);
              // Split "SEB 13" / "HPS 17" into a muted prefix + prominent number.
              const lm = label.match(/^([A-Za-z]+)\s*(.+)$/);
              const labelPrefix = isAvail ? "" : lm ? lm[1] : "";
              const labelValue = isAvail
                ? (availPct !== undefined ? `${availPct}%` : "–")
                : lm ? lm[2] : label;
              const corners = isAvail
                ? null
                : cornerLabels(info, isEx3, viewMode);
              // Dark text on light fills (amber/lime chains), white otherwise.
              const textFill = isLightColor(colour) ? "#1E293B" : "white";
              const isSelected = selectedSeatId === seatId;
              // Incident selections carry their own explicit seat set;
              // everything else follows the view-aware chain highlighting.
              const bright = brightSeats
                ? brightSeats.has(seatId)
                : isHighlighted(
                    selection,
                    { kind: "seat", seatId },
                    topology,
                    viewMode === "mcu" ? "mcu" : "data",
                  );
              const opacity = bright ? 1 : 0.15;

              return (
                <g
                  key={letter}
                  id={`seat-${seatId}`}
                  opacity={opacity}
                  style={{ transition: "opacity 180ms ease" }}
                  className={onSelect ? "cursor-pointer" : undefined}
                  onClick={
                    onSelect
                      ? (e) => {
                          e.stopPropagation();
                          onSelect({ kind: "seat", seatId });
                        }
                      : undefined
                  }
                  onDoubleClick={
                    onActivate
                      ? (e) => {
                          e.stopPropagation();
                          onActivate({ kind: "seat", seatId });
                        }
                      : undefined
                  }
                >
                  {/* Full chain path on hover — the tile itself shows only
                      the immediate parent to stay readable. */}
                  <title>
                    {seatTooltip(seatId, info, isEx3, topology) +
                      (availPct !== undefined ? ` · availability ${availPct}%` : "")}
                  </title>
                  <rect
                    x={xpos + 3}
                    y={yTop + 3}
                    width={cellW - 6}
                    height={cellH - 6}
                    fill={colour}
                    rx={4}
                    stroke={isSelected ? "#0f172a" : "none"}
                    strokeWidth={isSelected ? 2 : 0}
                  />
                  {corners && (
                    <>
                      <text
                        x={xpos + 6}
                        y={yTop + 13}
                        textAnchor="start"
                        fontSize={8.5}
                        fontWeight={700}
                        fill={textFill}
                        opacity={0.9}
                        letterSpacing="0.3"
                        pointerEvents="none"
                      >
                        {corners.parent}
                      </text>
                      <text
                        x={xpos + cellW - 6}
                        y={yTop + 13}
                        textAnchor="end"
                        fontSize={8.5}
                        fontWeight={700}
                        fill={textFill}
                        opacity={0.9}
                        letterSpacing="0.3"
                        pointerEvents="none"
                      >
                        {corners.port}
                      </text>
                    </>
                  )}
                  {/* De-emphasise the repeated prefix ("SEB"/"HPS"), keep the
                      identifying number large and readable. */}
                  <text
                    x={xpos + cellW / 2}
                    y={corners ? yTop + cellH / 2 + 9 : yTop + cellH / 2 + 4}
                    textAnchor="middle"
                    fill={textFill}
                    letterSpacing="0.2"
                    pointerEvents="none"
                  >
                    {labelPrefix && (
                      <tspan fontSize={7.5} fontWeight={600} opacity={0.72}>
                        {labelPrefix}{" "}
                      </tspan>
                    )}
                    <tspan fontSize={corners ? 13 : 12} fontWeight={700}>
                      {labelValue}
                    </tspan>
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  );
}
